import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DiscoveryProcessModule } from './discovery-process/discovery-process.module';
import { MoovCommonModule } from './moov-common/moov-common.module';
import { PersonalViewModule } from './personal-view/personal-view.module';
import { TeamViewModule } from './team-view/team-view.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { NavigationBarComponent } from './navigation-bar/navigation-bar.component';
import { AllMotivationsModule } from './all-motivations/all-motivations.module';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { LoginComponent } from './auth/components/login/login.component';
import { RegisterComponent } from './auth/components/register/register.component';
import { AuthModule } from './auth/auth/auth.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WindowReferenceModule } from './moov-common/modules/window-reference';
import * as interceptors from './moov-common/interceptors/http-token-interceptor';
import { LocalStorageService } from './moov-common/services/localStorage';
import { AuthorizationService } from './moov-common/services/authorization';
import { PersonalUserDataService } from './moov-common/services/personal-user-data/personal-user-data.service';
import {TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { LogoutMessageComponent } from './auth/components/logout-message/logout-message.component';
import { MakeMoovModule } from './make-moov/make-moov.module';
import { AppInjector } from './moov-common/app-injector';
import { AbstractPersonalUserDataService } from './moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { AbstractUserCoiService } from './moov-common/services/user-coi/user-coi.abstract.service.';
import { UserCoiService } from './moov-common/services/user-coi/user-coi.service';
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { AbstractLocalStorageService } from './moov-common/services/localStorage/local-storage.abstract.service';
import { MatStepperModule } from '@angular/material/stepper';
import { AbstractChallengesService } from './moov-common/services/challenge/challenges.abstract.service';
import { ChallengesService } from './moov-common/services/challenge/challenges.service';
import { AbstractConflictsService } from './moov-common/services/conflicts/conflict.abstract.service';
import { ConflictsService } from './moov-common/services/conflicts/conflict.service';
import { AbstractAuthorizationService } from './moov-common/services/authorization/authorization.abstract.service';
import { AbstractMotivationsService } from './moov-common/services/motivation/motivations.abstract.service';
import { MotivationsService } from './moov-common/services/motivation/motivations.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AbstractDiscoveryProcessService } from './discovery-process/discovery-process.abstract.service';
import { DiscoveryProcessService } from './discovery-process/discovery-process.service';
import { AbstractAnalyticsService } from './moov-common/services/google-analytics/analytics.abstract.service';
import { AnalyticsService } from './moov-common/services/google-analytics/analytics.service';
import { AbstractGuidedToursService } from './moov-common/services/guided-tours/guided-tours.abstract.service';
import { GuidedToursService } from './moov-common/services/guided-tours/guided-tours.service';
import { TokenLoginComponent } from './auth/components/token-login/token-login.component';
import { UserResponseComponent } from './user-response/user-response/user-response.component';
import { ResponsesService } from './moov-common/services/responses/responses.service';
import { ResponsesAbstractService } from './moov-common/services/responses/responses.abstract.service';
import { DetailsViewModule } from './details-view/details-view.module';
import { AdminViewModule } from './admin-view/admin-view.module';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { Locale } from './moov-common/enum/locale.enum';
import { LocalStorageKeys } from './moov-common/services/localStorage/local-storage.enum';
import { LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { StateViewModule } from './state-view/state-view.module';
import { AbstractDataService } from './moov-common/services/data/data.service.abstract';
import { DataService } from './moov-common/services/data/data.service';
import { UnsubscribeComponent } from './unsubscribe/unsubscribe.component';
import { ChatViewModule } from './chat-view/chat-view.module';
import { ChatService } from './moov-common/services/chat/chat.service';
import { AbstractChatService } from './moov-common/services/chat/chat.abstract.service';
import { ResellerViewModule } from './reseller-view/reseller-view.module';
import { NavigationSidebarComponent } from './navigation-sidebar/navigation-sidebar.component';
import { SidebarProcessStepsComponent } from './navigation-sidebar/sidebar-process-steps/sidebar-process-steps.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { MsalModule, MsalService, MSAL_INSTANCE, MsalRedirectComponent } from '@azure/msal-angular';
import { EnvConfigService } from './moov-common/services/env-config/env-config.service';
import { lastValueFrom } from 'rxjs';
import { EnvConfigKeys } from './moov-common/services/env-config/env-keys';
import { AbstractStructuredProcessService } from './moov-common/services/chat/structured-process.abstract.service';
import { StructuredProcessService } from './moov-common/services/chat/structured-process.service';
import localeHe from '@angular/common/locales/he';
import localeEn from '@angular/common/locales/en';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1
  || window.navigator.userAgent.indexOf('Trident/') > -1;

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

registerLocaleData(localeHe);
registerLocaleData(localeEn);

export function appInitializerFactory(translate: TranslateService, config: EnvConfigService) {
  // return () => new Promise<any>((resolve: any) => {

  //   const injector = AppInjector.getInjector();
  //   const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
  //   const localStorage : LocalStorageService  = injector.get(LocalStorageService);

  //   let userSelectedLocale : Locale | undefined= localStorage.getSecured<Locale>(LocalStorageKeys.userSelectedLocale);

  //   if (userSelectedLocale == undefined){
  //     userSelectedLocale = Locale.en_US;
  //   }
  
  //   config.loadConfig();

  //   locationInitialized.then(() => {
  //     const langToSet = 'en-US'
  //     translate.setDefaultLang('en-US');

  //     translate.use(userSelectedLocale!).subscribe({
  //       next: () => {
  //         // console.info(`Successfully initialized '${userSelectedLocale}' language.'`);
  //       },
  //       error: (err) => {
  //         console.error(`Problem with '${userSelectedLocale}' language initialization.`, err);
  //       },
  //       complete: () => {
  //         resolve(null);
  //       }
  //     });
  //   });
  // });

  return (): Promise<void> => {
    // console.log(`appInitializerFactory - starting`);
    // 1) Load external config
    return config.loadConfig()
      .then(() => {
        // console.log(`appInitializerFactory - config loaded`);
        const injector = AppInjector.getInjector();
        const locationInitialized: Promise<any> = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
        const localStorage: LocalStorageService = injector.get(LocalStorageService);

        // 2) Determine the user’s locale
        let userLocale: Locale = localStorage.getSecured<Locale>(LocalStorageKeys.userSelectedLocale)
          ?? Locale.en_US;

        // 3) Wait for the Angular router/location to initialize
        return locationInitialized.then(() => userLocale);
      })
      .then((userLocale: Locale) => {
        // console.log(`appInitializerFactory - userLocale: ${userLocale}`);
        // 4) Initialize Translate
        translate.setDefaultLang('en-US');
        // translate.use returns an Observable, so convert it to a Promise
        return lastValueFrom(translate.use(userLocale!));
      })
      .then(() => {
        // 5) All done—Angular can finish bootstrapping
        console.info('App initialization complete');
      })
      .catch(err => {
        console.error('App initialization failed', err);
        // Propagate failure so Angular stops bootstrapping
        return Promise.reject(err);
      });
  };  
}

export function MSALInstanceFactory(envConfigService: EnvConfigService) {
  console.log(`MSALInstanceFactory`);
  return new PublicClientApplication({
    auth: {
      clientId: envConfigService.get(EnvConfigKeys.auth0ClientId),
      // Deployments provisioned before MSAL_AUTHORITY existed fall back to the previous hardcoded value.
      authority: envConfigService.get(EnvConfigKeys.msalAuthority) ?? 'https://login.microsoftonline.com/common',
      redirectUri: envConfigService.get(EnvConfigKeys.auth0RedirectUri),
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) { return; }
          switch (level) {
            case LogLevel.Error: console.error(message); return;
            case LogLevel.Info:  console.info(message);  return;
            case LogLevel.Verbose: console.debug(message); return;
            case LogLevel.Warning: console.warn(message); return;
          }
        }
      }
    }
  });
}

const production = [
  { provide: AbstractPersonalUserDataService, useClass: PersonalUserDataService },
  { provide: AbstractUserCoiService, useClass: UserCoiService },
  { provide: AbstractLocalStorageService, useClass: LocalStorageService },
  { provide: AbstractChallengesService, useClass: ChallengesService },
  { provide: AbstractConflictsService, useClass: ConflictsService },
  { provide: AbstractAuthorizationService, useClass: AuthorizationService },
  { provide: AbstractMotivationsService, useClass: MotivationsService },
  { provide: AbstractDiscoveryProcessService, useClass: DiscoveryProcessService },
  { provide: AbstractAnalyticsService, useClass: AnalyticsService },
  { provide: AbstractGuidedToursService, useClass: GuidedToursService},
  { provide: ResponsesAbstractService, useClass: ResponsesService},
  { provide: AbstractDataService, useClass: DataService},
  { provide: AbstractChatService, useClass: ChatService},
  { provide: AbstractStructuredProcessService, useClass: StructuredProcessService}


  // AbstractChallengesServic
];

// const dev = [
//   { provide: AbstractPersonalUserDataService, useClass: MockPersonalUserDataService },
//   { provide: AbstractUserCoiService, useClass: MockUserCoiService }, 
//   { provide: AbstractLocalStorageService, useClass: LocalStorageService },
//   { provide: AbstractChallengesService, useClass: MockChallengesService },
//   { provide: AbstractConflictsService, useClass: MockConflictsService },
//   { provide: AbstractAuthorizationService, useClass: MockAuthorizationService },
//   { provide: AbstractMotivationsService, useClass: MockMotivationsService },
//   { provide: AbstractDiscoveryProcessService, useClass: MockDiscoveryProcessService },
//   { provide: AbstractGoogleAnalyticsService, useClass: MockGoogleAnalyticsService },
//   { provide: AbstractGuidedToursService, useClass: MockGuidedToursService },
//   { provide: ResponsesAbstractService, useClass: ResponsesMockService}

// ];

// const instance = dev;
// const userDataProvider = find(instance, function (provider) { return provider.provide.toString() == typeof (AbstractPersonalUserDataService); });

@NgModule({
  declarations: [
    AppComponent,
    NavigationBarComponent,
    LoginComponent,
    RegisterComponent,
    LogoutMessageComponent,
    TokenLoginComponent,
    UserResponseComponent,
    UnsubscribeComponent,
    NavigationSidebarComponent,
    SidebarProcessStepsComponent,
  ],
  exports: [
  ],
  imports: [
    ReactiveFormsModule,
    MatSidenavModule,
    BrowserModule,
    AppRoutingModule,
    MatMenuModule,
    MoovCommonModule,
    DiscoveryProcessModule,
    AllMotivationsModule,
    PersonalViewModule,
    TeamViewModule,
    AdminViewModule,
    ResellerViewModule,
    ChatViewModule,
    DetailsViewModule,
    MakeMoovModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    HttpClientModule,
    MatDialogModule,
    MatDividerModule,
    OverlayModule,
    MatButtonToggleModule,
    MatInputModule,
    AuthModule,
    FormsModule,
    MatListModule,
    MatSelectModule,
    MatStepperModule,
    StateViewModule,
    MsalModule,
    LoggerModule.forRoot({
      serverLoggingUrl: '/api/logs',
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.ERROR
    }),
    WindowReferenceModule.forBrowser(),
    TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        })
  ],
  providers: [...production,
    { provide: HTTP_INTERCEPTORS, useClass: interceptors.HttpTokenInterceptorService, multi: true },
    { provide: APP_INITIALIZER, useFactory: appInitializerFactory, deps: [TranslateService, EnvConfigService],multi: true },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory, deps: [EnvConfigService] },
    MsalService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})

export class AppModule {
    constructor(injector:Injector, private readonly iconRegistry: MatIconRegistry){
      AppInjector.setInjector(injector);// save a injector ref 💾
      const defaultFontSetClasses = iconRegistry.getDefaultFontSetClass();
      const outlinedFontSetClasses = defaultFontSetClasses
        .filter((fontSetClass) => fontSetClass !== 'material-icons')
        .concat(['material-symbols-outlined']);
      iconRegistry.setDefaultFontSetClass(...outlinedFontSetClasses);    
    }
}